import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MAX_UPLOAD_BYTES, UPLOAD_TOO_LARGE_MESSAGE } from "../../api/documentsApi";

const ALLOWED_MIME = new Set<string>([
  "image/png", "image/jpeg", "image/gif", "image/webp",
  "application/pdf", "text/plain", "text/csv", "text/markdown",
  "application/json",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]);
const ALLOWED_EXT = /\.(png|jpe?g|gif|webp|pdf|txt|csv|md|json|docx|xlsx|pptx|ya?ml|ts|tsx|js|jsx|py|sql)$/i;

export interface UploadApi {
  upload(input: { file: File; tier?: string; intent?: string }): Promise<{ document_id: number; status: string }>;
  get(id: number): Promise<{ id: number; status: string; error?: string | null }>;
}

export type AttachmentStatus = "uploading" | "ready" | "error";

export interface Attachment {
  localId: string;
  name: string;
  mime: string;
  size: number;
  status: AttachmentStatus;
  documentId?: number;
  error?: string;
}

export interface UseAttachmentsArgs {
  api: UploadApi;
  pollIntervalMs?: number;
  timeoutMs?: number;
}

function validate(f: File): string | null {
  if (f.size > MAX_UPLOAD_BYTES) return UPLOAD_TOO_LARGE_MESSAGE;
  const okMime = f.type && ALLOWED_MIME.has(f.type);
  const okExt = ALLOWED_EXT.test(f.name);
  if (!okMime && !okExt) return "Unsupported file type";
  return null;
}

export function useAttachments({ api, pollIntervalMs = 1200, timeoutMs = 60000 }: UseAttachmentsArgs) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const liveRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    return () => { liveRef.current.clear(); };
  }, []);

  const patch = useCallback((localId: string, p: Partial<Attachment>) => {
    setAttachments((prev) => prev.map((a) => (a.localId === localId ? { ...a, ...p } : a)));
  }, []);

  const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

  const uploadOne = useCallback(
    async (att: Attachment, f: File) => {
      try {
        const { document_id } = await api.upload({ file: f, tier: "user", intent: "attachment" });
        if (!liveRef.current.has(att.localId)) return;
        patch(att.localId, { documentId: document_id });
        const deadline = Date.now() + timeoutMs;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (!liveRef.current.has(att.localId)) return;
          const row = await api.get(document_id);
          if (row.status === "ready" || row.status === "extracted") { patch(att.localId, { status: "ready" }); return; }
          if (row.status === "failed") { patch(att.localId, { status: "error", error: row.error ?? "Processing failed" }); return; }
          if (Date.now() > deadline) { patch(att.localId, { status: "error", error: "Still processing — remove or retry" }); return; }
          await sleep(pollIntervalMs);
        }
      } catch (e) {
        if (!liveRef.current.has(att.localId)) return;
        patch(att.localId, { status: "error", error: e instanceof Error ? e.message : "Upload failed" });
      }
    },
    [api, patch, pollIntervalMs, timeoutMs],
  );

  const add = useCallback(
    async (files: File[]) => {
      const created: Array<{ att: Attachment; file: File | null }> = files.map((f) => {
        const localId = crypto.randomUUID();
        const err = validate(f);
        if (!err) liveRef.current.add(localId);
        const att: Attachment = {
          localId, name: f.name, mime: f.type, size: f.size,
          status: err ? "error" : "uploading",
          error: err ?? undefined,
        };
        return { att, file: err ? null : f };
      });
      setAttachments((prev) => [...prev, ...created.map((c) => c.att)]);
      await Promise.all(created.filter((c) => c.file).map((c) => uploadOne(c.att, c.file as File)));
    },
    [uploadOne],
  );

  const remove = useCallback((localId: string) => {
    liveRef.current.delete(localId);
    setAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const retry = useCallback((localId: string) => { remove(localId); }, [remove]);

  const clear = useCallback(() => {
    setAttachments((prev) => { prev.forEach((a) => liveRef.current.delete(a.localId)); return []; });
  }, []);

  const uploading = attachments.some((a) => a.status === "uploading");
  const allReady = !uploading;
  const attachmentIds = useMemo(
    () => attachments.filter((a) => a.status === "ready" && a.documentId != null).map((a) => String(a.documentId)),
    [attachments],
  );

  return { attachments, add, remove, retry, clear, uploading, allReady, attachmentIds };
}
